/**
 * Shared layout primitive for sub-header section components.
 *
 * Used by ContactSubHeader / CompanySubHeader / DealSubHeader to render
 * their three-part header row:
 *
 *   primary | secondary   tertiary
 *
 * MeetingSubHeader and EmailThreadSubHeader render their own layout because
 * their content shapes (time + join button, single visibility badge) don't
 * fit this pattern.
 *
 * Internal to `record/sections/`; no consumers outside the per-type
 * sub-header components should import this directly.
 */

import type { ReactNode } from 'react';

interface SubHeaderLayoutProps {
  /** Left-most value (e.g. job title, domain, stage). */
  primary?: ReactNode;
  /** Middle value, separated from primary by `|` (e.g. company, owner). */
  secondary?: ReactNode;
  /** Right-most value, separated from secondary by whitespace. */
  tertiary?: ReactNode;
}

export function SubHeaderLayout({
  primary,
  secondary,
  tertiary,
}: SubHeaderLayoutProps) {
  if (!primary && !secondary && !tertiary) {
    return null;
  }

  return (
    <div className='flex items-center flex-wrap gap-2.5 text-xs md:text-sm'>
      {primary && (
        <span className='flex items-center text-fg-primary'>{primary}</span>
      )}
      {secondary && (
        <>
          {primary && <span className='text-fg-quaternary'>|</span>}
          <span className='flex items-center'>{secondary}</span>
        </>
      )}
      {tertiary && <span className='flex items-center'>{tertiary}</span>}
    </div>
  );
}
